<template>
    <div class="form-outline">
      <label class="label" :class="{ disabled: isDisabled }">
        <span v-if="!!label">{{ label }}</span>
        <span v-else>{{ $t("tourClient.selectPax") }}*</span>
      </label>
  
      <v-select
        v-if="status === 'loaded'"
        v-model="localValue"
        :disabled="isDisabled"
        :clear-search-on-select="true"
        :clearable="clearable"
        :filterable="false"
        :options="paxs"
        :reduce="!returnObject ? ((pax: any) => pax.id) : undefined"
        class="select-search"
        label="name"
        @search="onSearch"
        @update:model-value="$emit('update:modelValue', localValue)"
      >
        <template v-slot:no-options>
          <div class="my-4">{{ $t("tourClient.writeSearchPax") }}</div>
        </template>
  
        <template v-slot:option="option">
          <div class="d-center">
            <span>{{ option?.name }} {{ option?.surname || "" }} {{ option?.surname_1 || "" }} {{ option?.surname_2 || "" }} & {{ option?.email }}</span>
          </div>
        </template>
  
        <template v-slot:selected-option="option">
          <div class="selected d-center py-8">
            <span>{{ option?.name }} {{ option?.surname || "" }} {{ option?.surname_1 || "" }} {{ option?.surname_2 || "" }} & {{ option?.email }}</span>
          </div>
        </template>
  
        <template #open-indicator="{ attributes }">
          <span v-bind="attributes">
            <v-icon icon="search" size="sm" />
          </span>
        </template>
  
        <template v-if="canCreate" #list-footer>
          <li ref="load" class="create-client">
            <v-button
              class="btn-icon h-fit fw-normal shadow-none py-0 px-16"
              size="sm"
              variant="link"
              @click="openModal('v-pax-create-modal')"
            >
              <v-icon icon="plus" size="xs" space="me-8" />
              {{ $t("clientTour.createPax") }}
            </v-button>
          </li>
        </template>
      </v-select>
  
      <v-pax-create-modal
        v-if="modalToShow === 'v-pax-create-modal'"
        :agency="agency"
        @client="setNewPax"
        @closed="closeModal"
        :case-day="caseDay"
        :case-id="caseId"
        @reload-case="$emit('reloadCase')"
      />
    </div>
  </template>
  
  <script lang="ts">
  import { defineComponent } from "vue";
  import vSelect from "vue-select";
  import api from "@/api";
  import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
  import Pax from "@/api/objects/Pax";
  import useModalUtils from "@/helpers/ModalUtils";
  import VButton from "@/components/vendor/basic/button/VButton.vue";
  import VPaxCreateModal from "@/components/shared/VPaxCreateModal.vue";
  
  export default defineComponent({
    name: "VSelectPaxSearch",
    components: {
      VPaxCreateModal,
      VIcon,
      vSelect,
      VButton,
    },
    emits: ["userAttached", "clear", "update:modelValue", "reloadCase"],
    props: {
      modelValue: {
        type: [String, Number],
        required: false,
      },
      isDisabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      label: {
        type: String,
        required: false,
        default: "",
      },
      clientName: {
        type: String,
        required: false,
      },
      clientId: {
        type: Number,
        required: false,
      },
      canCreate: {
        type: Boolean,
        required: false,
        default: true,
      },
      clearable: {
        type: Boolean,
        required: false,
        default: true,
      },
      agency: {
        type: String,
        required: true,
      },
      returnObject: {
        type: Boolean,
        required: false,
        default: false,
      },
      caseDay: {
        type: {} as any,
        default: null,
      },
      caseId: {
        type: Number,
        required: false,
        default: null,
      },
    },
    setup() {
      return {
        ...useModalUtils(),
      };
    },
    data() {
      return {
        localValue: this.modelValue,
        paxs: [] as Pax[],
        status: "loading" as string,
      };
    },
    async created() {
      this.status = "loading";
  
      if (!!this.clientName) {
        await this.loadPaxs(null, this.clientName);
      }
  
      if (!!this.clientId) {
        await this.loadPaxs(null, "", true);
      }
  
      this.status = "loaded";
    },
    methods: {
      onSearch(search: string, loading: any) {
        if (search.length) {
          loading(true);
          this.loadPaxs(loading, search);
        }
      },
      async loadPaxs(loading?: any, search?: string, withDefault?: boolean) {
        if (!!loading) {
          loading(true);
        }
  
        try {
          if (!search) {
            search = "";
          }
  
          let id: string | number = "";
          if (withDefault && !!this.clientId) {
            id = this.clientId;
          }

          const query = { name: search, id };

          const response: any = await api.pax.paxAll(this.agency, query);
          const rawPaxs = response?.content?.data || response?.content || response || [];

          this.paxs = rawPaxs.map((item: any) => {
            // If item itself is the pax, use it. If it's a pivot, extract .pax
            const actualPax = item.pax || item;
            return {
              ...actualPax,
              id: item.id || actualPax.id,
              name: actualPax.name,
              surname: actualPax.surname,
              surname_1: actualPax.surname_1,
              surname_2: actualPax.surname_2,
              email: actualPax.email,
            };
          });
        } catch (e: any) {
          console.error(e);
  
          this.$toast.error(e.response?.data?.message || "An error occurred");
        } finally {
          if (!!loading) {
            loading(false);
          }
        }
      },
      async setNewPax(pax: any) {
        this.localValue = pax.id;
  
        await this.loadPaxs(null, pax.name);
  
        this.$emit("update:modelValue", pax.id);
      },
    },
  });
  </script>
  